<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import DatePicker from 'primevue/datepicker'
import FloatLabel from 'primevue/floatlabel'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import { useRemindersStore } from '@/stores/reminders.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  targetAppLabel: { type: String, required: true },
  targetModel: { type: String, required: true },
  targetObjectId: { type: [Number, String], required: true },
  channel: { type: String, default: 'in_app' },
  timezoneName: { type: String, default: Intl.DateTimeFormat().resolvedOptions().timeZone },
})
const emit = defineEmits(['update:modelValue', 'created'])
const toast = useToast()
const reminders = useRemindersStore()

const visible = ref(props.modelValue)
watch(() => props.modelValue, v => visible.value = v)
watch(visible, v => emit('update:modelValue', v))

const when = ref(null) // Date
const text = ref('')
const saving = ref(false)

const payload = computed(() => {
  if (!when.value) return null
  return {
    text: (text.value || '').trim(),
    trigger_at: new Date(when.value).toISOString(),
    timezone_name: props.timezoneName || undefined,
    channel: props.channel || undefined,
    target_app_label: props.targetAppLabel,
    target_model: props.targetModel,
    target_object_id: Number(props.targetObjectId),
  }
})

async function submit() {
  if (!payload.value) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Укажите дату и время', life: 2500 })
    return
  }
  if (!payload.value.text) {
    toast.add({ severity: 'warn', summary: 'Проверьте форму', detail: 'Добавьте текст напоминания', life: 2500 })
    return
  }
  saving.value = true
  try {
    const created = await reminders.create(payload.value)
    toast.add({ severity: 'success', summary: 'Напоминание создано', life: 2000 })
    emit('created', created)
    visible.value = false
    when.value = null
    text.value = ''
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка создания', detail: e?.message || 'Не удалось создать', life: 4000 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" header="Создать напоминание" :modal="true" :style="{ width: '320px' }" position="top">
    <form @submit.prevent="submit" class="flex flex-col gap-6 p-fluid pt-2">
      <FloatLabel variant="on">
        <DatePicker id="datepicker-24h" v-model="when" showTime hourFormat="24" fluid />
        <label for="datepicker-24h">Когда</label>
      </FloatLabel>

      <FloatLabel variant="on">
        <Textarea id="reminder_text" v-model="text" autoResize rows="3" class="w-full"/>
        <label for="reminder_text">Текст</label>
      </FloatLabel>

      <div class="flex justify-center gap-4 pt-2">
        <Button label="Создать" type="submit" severity="info" rounded :loading="saving" />
        <Button label="Отмена" type="button" severity="secondary" rounded :disabled="saving" @click="visible=false" />
      </div>
    </form>
  </Dialog>
</template>